/**
 * Styles compartilhados do modulo WhatsApp.
 *
 * Filosofia: reusar Pill, Card, Select, Table de pages/Orcamentos/styles
 * (design system pos-RBAC). So criar styles especificos pro chat (split-
 * view, bubbles, composer) que nao tem equivalente em outras telas.
 */
import styled from "styled-components";

// ---------- Layout do Inbox (split-view) ----------

export const InboxLayout = styled.div`
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 16px;
  height: calc(100vh - 220px);
  min-height: 480px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

export const ConversasPane = styled.section`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.surface || "#fff"};
  border: 1px solid ${({ theme }) => theme.border || "#e5e7eb"};
  border-radius: 12px;
  overflow: hidden;

  @media (max-width: 920px) {
    height: auto;
    max-height: 480px;
  }
`;

export const ConversasHeader = styled.header`
  padding: 12px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.border || "#e5e7eb"};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ConversasFiltros = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

export const ConversasScroll = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
`;

export const ConversaItemRow = styled.button.attrs({ type: "button" })`
  display: grid;
  grid-template-columns: 40px 1fr auto;
  gap: 10px;
  align-items: center;
  width: 100%;
  padding: 12px 14px;
  border: none;
  background: ${({ $ativo, theme }) =>
    $ativo ? theme.surfaceAlt || "#f3f4f6" : "transparent"};
  border-left: 3px solid
    ${({ $ativo, theme }) => ($ativo ? theme.accent || "#0ea5e9" : "transparent")};
  text-align: left;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.borderSubtle || "#f1f5f9"};

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt || "#f9fafb"};
  }
`;

export const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ theme }) => theme.accentSurface || "#dbeafe"};
  color: ${({ theme }) => theme.accent || "#1d4ed8"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
`;

export const ConversaInfo = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  .nome {
    font-weight: 600;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${({ theme }) => theme.text || "#0f172a"};
  }

  .preview {
    font-size: 12px;
    color: ${({ theme }) => theme.textMuted || "#64748b"};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const ConversaMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;

  .quando {
    font-size: 11px;
    color: ${({ theme }) => theme.textMuted || "#94a3b8"};
  }
`;

// ---------- Chat ----------

export const ChatPane = styled.section`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.surface || "#fff"};
  border: 1px solid ${({ theme }) => theme.border || "#e5e7eb"};
  border-radius: 12px;
  overflow: hidden;
`;

export const ChatHeader = styled.header`
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.border || "#e5e7eb"};
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  .info {
    flex: 1 1 auto;
    min-width: 0;
  }
  .nome {
    font-weight: 600;
    font-size: 14px;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .submeta {
    font-size: 12px;
    color: ${({ theme }) => theme.textMuted || "#64748b"};
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
  }
`;

export const ChatMessages = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 16px;
  background: ${({ theme }) => theme.surfaceAlt || "#f9fafb"};
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const BubbleRow = styled.div`
  display: flex;
  width: 100%;
  ${({ $direction }) =>
    $direction === "OUTBOUND"
      ? "justify-content: flex-end;"
      : "justify-content: flex-start;"}
`;

export const Bubble = styled.div`
  max-width: 70%;
  padding: 8px 12px;
  border-radius: 12px;
  background: ${({ $direction, theme }) =>
    $direction === "OUTBOUND"
      ? theme.accentSurface || "#dcfce7"
      : theme.surface || "#fff"};
  border: 1px solid ${({ theme }) => theme.borderSubtle || "#e5e7eb"};
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  position: relative;

  .texto {
    color: ${({ theme }) => theme.text || "#0f172a"};
  }
  .meta {
    margin-top: 4px;
    display: flex;
    gap: 6px;
    align-items: center;
    font-size: 10px;
    color: ${({ theme }) => theme.textMuted || "#64748b"};
  }
`;

export const ChatEmpty = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: ${({ theme }) => theme.textMuted || "#94a3b8"};
  font-size: 14px;
  padding: 32px;
  text-align: center;
`;

// ---------- Composer ----------

export const ComposerWrap = styled.div`
  border-top: 1px solid ${({ theme }) => theme.border || "#e5e7eb"};
  padding: 10px 12px;
  background: ${({ theme }) => theme.surface || "#fff"};
  flex-shrink: 0;
`;

export const ComposerHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.textMuted || "#94a3b8"};
  margin-bottom: 6px;
`;

export const ComposerInputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  align-items: flex-end;

  textarea {
    width: 100%;
    min-height: 38px;
    max-height: 160px;
    padding: 8px 10px;
    border: 1px solid ${({ theme }) => theme.border || "#cbd5e1"};
    border-radius: 8px;
    resize: vertical;
    font-family: inherit;
    font-size: 13px;
    background: ${({ theme }) => theme.surface || "#fff"};
    color: ${({ theme }) => theme.text || "#0f172a"};

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.accent || "#0ea5e9"};
    }
    &:disabled {
      background: ${({ theme }) => theme.surfaceMuted || "#f1f5f9"};
      cursor: not-allowed;
    }
  }
`;

export const ComposerActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const ComposerCounter = styled.div`
  font-size: 11px;
  color: ${({ theme, $warning }) =>
    $warning ? theme.warning || "#ea580c" : theme.textMuted || "#94a3b8"};
  text-align: right;
`;

// ---------- QR modal ----------

export const QrFrame = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 16px;

  img {
    width: 240px;
    height: 240px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.border || "#e5e7eb"};
    image-rendering: pixelated;
  }

  .countdown {
    font-size: 12px;
    color: ${({ theme }) => theme.textMuted || "#64748b"};
  }
`;

// ---------- Kill switch toggle ----------

export const KillSwitchBox = styled.div`
  border: 1px solid
    ${({ $pausado, theme }) =>
      $pausado ? theme.warningBorder || "#fcd34d" : theme.border || "#e5e7eb"};
  background: ${({ $pausado, theme }) =>
    $pausado ? theme.warningSurface || "#fef3c7" : theme.surfaceAlt || "#f9fafb"};
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

// ---------- Tag input (alerta_email) ----------

export const TagInputWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.border || "#cbd5e1"};
  border-radius: 8px;
  min-height: 38px;
  background: ${({ theme }) => theme.surface || "#fff"};

  input {
    flex: 1;
    min-width: 120px;
    border: none;
    outline: none;
    font-size: 13px;
    background: transparent;
    color: ${({ theme }) => theme.text || "#0f172a"};
  }
`;

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${({ theme }) => theme.accentSurface || "#dbeafe"};
  color: ${({ theme }) => theme.accent || "#1d4ed8"};
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;

  button {
    border: none;
    background: transparent;
    cursor: pointer;
    color: inherit;
    padding: 0;
    font-size: 14px;
    line-height: 1;
  }
`;
